import React, { createElement, FC } from "react";
import { createPortal } from "react-dom";
import { ComponentContainer } from "./ComponentContainer";

interface ViewComponentProps {
  id: string;
  selector: string;
  component: FC;
  requiredProps?: readonly string[];
}

/**
 * Get selector, find entrypoint at page and create component instance
 */
export const ViewComponent: FC<ViewComponentProps> = ({ id, selector, component, requiredProps }) => {
  // Get all entrypoint for component
  const portalElements = document.querySelectorAll(selector);

  // Check if entrypoint exists
  if (portalElements.length === 0) {
    console.warn(`Cannot find entrypoint ${selector} for component ${component.displayName}`);
    return null;
  }

  // Set every entrypoint id (redrawing optimisation)
  portalElements.forEach((element, i) => {
    element.setAttribute("id", element.id ?? `${id}-${i}`);
  });

  return (
    <>
      {Array.from(portalElements).map((entryPoint: Element) => {
        const elementProps = JSON.parse((entryPoint as HTMLElement).dataset.props ?? "{}");

        elementProps["portalElement"] = entryPoint;

        // Check if required props are provided
        if (requiredProps && requiredProps.some((prop) => elementProps[prop] === undefined)) {
          if (import.meta.env.MODE === "development") {
            const missingProp = requiredProps.find((prop) => elementProps[prop] === undefined);
            throw new Error(
              `Missing required property "${String(missingProp)}" for component "${component.name}" with selector "${selector}"`
            );
          } else {
            throw new Error(`View component missing required property`);
          }
        }

        return createPortal(
          <ComponentContainer key={entryPoint.id}>{createElement(component, { ...elementProps })}</ComponentContainer>,
          entryPoint
        );
      })}
    </>
  );
};
